import { Cluster, MarkerClusterer, Renderer } from '@googlemaps/markerclusterer';
import { MapListing } from '../../models/map-listing.model';
import { track } from '../../services/analytics';
import { buildCategoryPin, buildClusterPin, buildGroupPin } from './category-pin';

// Radius of the circle the co-located pins fan out onto when a group is expanded.
// In meters, so at a lower fan-out zoom it covers fewer pixels; kept generous so
// the fanned pins stay visually separated at EXPAND_ZOOM.
const SPIDERFY_RADIUS_METERS = 75;

// Zoom at/above which SuperCluster stops clustering (default is 16). We set it
// explicitly so we can reason about it: at any zoom > CLUSTER_MAX_ZOOM every
// marker - including a co-located group's anchor - is guaranteed to render
// standalone (never inside a cluster bubble). That is exactly when it is safe to
// auto fan-out the group, so EXPAND_ZOOM sits one step past it.
const CLUSTER_MAX_ZOOM = 14;
const EXPAND_ZOOM = CLUSTER_MAX_ZOOM + 1;

// Padding for every programmatic fit, in canvas pixels. Top clears the fixed 80px
// site nav that the map underlaps; the rest is breathing room (the plan sheet's
// 72px peek is already cut off by .planner-map's padding-bottom). Shared with the
// component's opening fit on Serbia so no fit ever parks pins under the chrome.
export const MAP_FIT_PADDING = { top: 96, right: 24, bottom: 24, left: 24 };

// Ceiling for tap-driven zoom. Without it fitBounds slams to the map's maximum
// whenever the target is tight - a cluster of near-identical coordinates, or a
// group, whose bounds is a single point - and the pins land off screen. EXPAND_ZOOM
// is the natural stop: clustering has ended and a group's fan-out is legible there.
const CLICK_ZOOM_CAP = EXPAND_ZOOM;

interface PinGroup {
  key: string;
  listings: MapListing[];
  anchor: google.maps.marker.AdvancedMarkerElement;
  expanded: boolean;
  // Spider legs drawn from the anchor to each fanned-out leaf while expanded.
  legs: google.maps.Polyline[];
}

export interface MarkerLayerCallbacks {
  onListingClick: (listingId: number) => void;
  // Called just before the layer moves the map itself, so gesture tracking can
  // tell our own animations apart from the visitor's pans and zooms.
  onProgrammaticMove: () => void;
}

// Owns the whole pin layer for the planner map: category leaf markers, proximity
// clustering, and the custom spiderfier for pins that share an exact coordinate.
// Kept out of MapViewComponent so the component only wires the map, route, and
// start marker. Instantiate after the map and gmaps libraries are ready, then
// drive it with setListings()/updateBadges(); call destroy() on teardown.
export class MarkerLayer {
  private clusterer: MarkerClusterer | null = null;
  private leafMarkersById = new Map<number, google.maps.marker.AdvancedMarkerElement>();
  private groups: PinGroup[] = [];
  // Weight of each marker handed to the clusterer, so a cluster's rendered count
  // reflects the true number of listings: a singleton leaf is 1, a co-located
  // group's anchor is the size of the group. Missing entry defaults to 1.
  private markerWeights = new WeakMap<google.maps.marker.AdvancedMarkerElement, number>();
  private mapListeners: google.maps.MapsEventListener[] = [];
  private listings: MapListing[] = [];

  constructor(
    private readonly map: google.maps.Map,
    private readonly markerLib: google.maps.MarkerLibrary,
    private readonly geometryLib: google.maps.GeometryLibrary,
    private readonly callbacks: MarkerLayerCallbacks
  ) {
    this.mapListeners.push(
      this.map.addListener('zoom_changed', () => this.syncGroupExpansionToZoom())
    );
    // Clicking empty map collapses manually-opened groups. Above EXPAND_ZOOM the
    // groups are zoom-driven, so a stray map click must not collapse them.
    this.mapListeners.push(
      this.map.addListener('click', () => {
        if (this.currentZoom() < EXPAND_ZOOM) this.collapseAllGroups();
      })
    );
  }

  // Rebuilds the whole marker layer from a listing set. Called on initial load
  // and every filter re-fetch.
  setListings(listings: MapListing[], stopIds: Set<number>): void {
    this.listings = listings;
    this.clearMarkers();

    // Bucket listings by exact coordinate. A leaf marker is created for every
    // listing; only singletons and group anchors go to the clusterer so
    // co-located listings stay reachable via spiderfy.
    const byCoord = new Map<string, MapListing[]>();
    for (const listing of listings) {
      const key = `${listing.lat.toFixed(6)},${listing.lng.toFixed(6)}`;
      const bucket = byCoord.get(key);
      if (bucket) bucket.push(listing);
      else byCoord.set(key, [listing]);
    }

    const clusterMarkers: google.maps.marker.AdvancedMarkerElement[] = [];
    for (const [key, group] of byCoord) {
      for (const listing of group) {
        this.leafMarkersById.set(listing.id, this.buildLeafMarker(listing, stopIds));
      }

      if (group.length === 1) {
        const leaf = this.leafMarkersById.get(group[0].id)!;
        this.markerWeights.set(leaf, 1);
        clusterMarkers.push(leaf);
        continue;
      }

      const anchor = new this.markerLib.AdvancedMarkerElement({
        position: { lat: group[0].lat, lng: group[0].lng },
        title: group.map((l) => l.title).join(', '),
        content: buildGroupPin(group.length, { inPlan: this.groupInPlan(group, stopIds) }),
        gmpClickable: true,
      });
      const pinGroup: PinGroup = { key, listings: group, anchor, expanded: false, legs: [] };
      anchor.addListener('gmp-click', () => this.toggleGroup(pinGroup));
      this.groups.push(pinGroup);
      this.markerWeights.set(anchor, group.length);
      clusterMarkers.push(anchor);
    }

    this.clusterer = new MarkerClusterer({
      map: this.map,
      markers: clusterMarkers,
      algorithmOptions: { maxZoom: CLUSTER_MAX_ZOOM },
      renderer: this.clusterRenderer(),
      // Replaces defaultOnClusterClickHandler, which is a bare
      // map.fitBounds(cluster.bounds): no padding, so a fit parks pins under the
      // fixed header, and no ceiling, so a tight cluster jumps to maximum zoom.
      onClusterClick: (_event, cluster) => {
        track('planner_cluster_open', { count: this.clusterWeight(cluster) });
        if (cluster.bounds) this.zoomToBounds(cluster.bounds);
      },
    });

    // New groups must immediately match the current zoom (e.g. re-fetch while
    // already zoomed in should show the fanned-out state).
    this.syncGroupExpansionToZoom();
  }

  // Refreshes pin appearance (in-plan check badge) without rebuilding markers.
  updateBadges(stopIds: Set<number>): void {
    for (const listing of this.listings) {
      const mk = this.leafMarkersById.get(listing.id);
      if (!mk) continue;
      mk.content = buildCategoryPin(listing.category_slug, { inPlan: stopIds.has(listing.id) });
    }
    for (const group of this.groups) {
      group.anchor.content = buildGroupPin(group.listings.length, {
        inPlan: this.groupInPlan(group.listings, stopIds),
      });
    }
  }

  destroy(): void {
    this.clearMarkers();
    for (const l of this.mapListeners) l.remove();
    this.mapListeners = [];
  }

  // A cluster's count is the number of markers it holds, but a group anchor
  // stands in for several listings. Sum the per-marker weights so the bubble
  // shows the true listing total (e.g. a 5-group + 3 singletons reads "8").
  private clusterWeight(cluster: Cluster): number {
    let total = 0;
    for (const marker of cluster.markers ?? []) {
      total += this.markerWeights.get(marker as google.maps.marker.AdvancedMarkerElement) ?? 1;
    }
    return total;
  }

  private clusterRenderer(): Renderer {
    return {
      render: (cluster) => {
        const total = this.clusterWeight(cluster);
        return new this.markerLib.AdvancedMarkerElement({
          position: cluster.position,
          content: buildClusterPin(total),
          zIndex: 1000 + total,
          // Required, and easy to miss here: MarkerClusterer binds its zoom-in
          // handler with addListener('gmp-click'), but an AdvancedMarkerElement
          // ignores pointer events unless gmpClickable is set (it defaults to
          // false, and attaching a listener does not flip it). Without this the
          // bubbles are inert - which is most of the opening country-wide view.
          gmpClickable: true,
        });
      },
    };
  }

  // Tears down every marker/group/spider-leg so the layer can be rebuilt.
  private clearMarkers(): void {
    this.clusterer?.clearMarkers();
    this.clusterer = null;
    for (const mk of this.leafMarkersById.values()) mk.map = null;
    this.leafMarkersById.clear();
    for (const g of this.groups) {
      g.anchor.map = null;
      this.clearLegs(g);
    }
    this.groups = [];
  }

  private buildLeafMarker(
    listing: MapListing,
    stopIds: Set<number>
  ): google.maps.marker.AdvancedMarkerElement {
    const mk = new this.markerLib.AdvancedMarkerElement({
      position: { lat: listing.lat, lng: listing.lng },
      title: listing.title,
      content: buildCategoryPin(listing.category_slug, { inPlan: stopIds.has(listing.id) }),
      gmpClickable: true,
    });
    mk.addListener('gmp-click', () => this.callbacks.onListingClick(listing.id));
    return mk;
  }

  private groupInPlan(listings: MapListing[], stopIds: Set<number>): boolean {
    return listings.some((l) => stopIds.has(l.id));
  }

  // Expands every co-located group once zoomed past EXPAND_ZOOM (where anchors
  // are guaranteed to be un-clustered), collapses them all below it.
  private syncGroupExpansionToZoom(): void {
    if (this.currentZoom() >= EXPAND_ZOOM) {
      for (const group of this.groups) {
        if (!group.expanded) this.expandGroup(group);
      }
    } else {
      this.collapseAllGroups();
    }
  }

  // Only the click path reports: expandGroup is also driven by zoom from
  // syncGroupExpansionToZoom, and a fan-out the visitor did not ask for is not a
  // tap on a group.
  private toggleGroup(group: PinGroup): void {
    if (group.expanded) {
      this.collapseGroup(group);
      return;
    }
    track('planner_group_open', { count: group.listings.length });
    // A group's listings share one coordinate, so its bounds is a point and the
    // fit lands exactly on CLICK_ZOOM_CAP - the zoom at which the fan-out is
    // legible. Same call as a cluster tap, so the two gestures feel identical.
    const pos = new google.maps.LatLng(group.listings[0].lat, group.listings[0].lng);
    this.zoomToBounds(new google.maps.LatLngBounds(pos, pos));
    this.expandGroup(group);
  }

  // Single path for both tap-to-zoom gestures. maxZoom is set for the duration of
  // the fit rather than corrected afterwards, which would overshoot the target and
  // visibly snap back; 'idle' always follows a fit, so the ceiling is released.
  private zoomToBounds(bounds: google.maps.LatLngBounds): void {
    this.callbacks.onProgrammaticMove();
    // The cap is a ceiling on how far a tap may zoom IN, never a level to snap
    // to. A visitor who has already zoomed in past it only gets re-centred -
    // fitting here would drag them back out, which reads as the map fighting them.
    if (this.currentZoom() >= CLICK_ZOOM_CAP) {
      this.map.panTo(bounds.getCenter());
      return;
    }
    this.map.setOptions({ maxZoom: CLICK_ZOOM_CAP });
    this.map.fitBounds(bounds, MAP_FIT_PADDING);
    google.maps.event.addListenerOnce(this.map, 'idle', () => {
      this.map.setOptions({ maxZoom: null });
    });
  }

  private expandGroup(group: PinGroup): void {
    const anchorPos = new google.maps.LatLng(group.listings[0].lat, group.listings[0].lng);
    const n = group.listings.length;
    group.listings.forEach((listing, i) => {
      const heading = (360 / n) * i;
      const pos = this.geometryLib.spherical.computeOffset(
        anchorPos,
        SPIDERFY_RADIUS_METERS,
        heading
      );
      const leaf = this.leafMarkersById.get(listing.id);
      if (!leaf) return;
      leaf.position = pos;
      leaf.map = this.map;

      group.legs.push(
        new google.maps.Polyline({
          map: this.map,
          path: [anchorPos, pos],
          strokeColor: '#9ca3af',
          strokeWeight: 1.5,
          strokeOpacity: 0.6,
          clickable: false,
          zIndex: 1,
        })
      );
    });

    // The anchor deliberately stays visible: it is a marker owned by the
    // clusterer, which re-adds it on the next map 'idle' no matter what we set
    // here. Hiding it produced a flicker and left the fan-out sitting under a
    // grey pin that had silently come back. Kept on screen it reads as the
    // centre of the spider and doubles as the collapse target.
    group.expanded = true;
  }

  private collapseGroup(group: PinGroup): void {
    for (const listing of group.listings) {
      const leaf = this.leafMarkersById.get(listing.id);
      if (leaf) leaf.map = null;
    }
    this.clearLegs(group);
    group.expanded = false;
  }

  private collapseAllGroups(): void {
    for (const group of this.groups) {
      if (group.expanded) this.collapseGroup(group);
    }
  }

  private clearLegs(group: PinGroup): void {
    for (const leg of group.legs) leg.setMap(null);
    group.legs = [];
  }

  private currentZoom(): number {
    return this.map.getZoom() ?? 0;
  }
}
